<template>
  <fragment>
    <mc-app-header title="Review User Challenge Data"></mc-app-header>
    <mc-app-body :submitting="challengeAttempts.submitting">
      <div class="review-challenge-attempt section">
        <b-loading
          v-if="challengeAttempts.loadingSelected || challenges.loadingSelected"
          active
          :is-full-page="false"
        />
        <mc-error-layout
          v-else-if="challengeAttempts.selectError"
          message="Failed to load Submission!"
          :retry-fn="() => selectChallengeAttempt($route.params.id)"
        />
        <div v-else :set="(challengeAttempt = challengeAttempts.selected)">
          <div class="container challenge-attempt">
            <section class="challenge-attempt__info tile is-ancestor">
              <div class="tile is-4 is-vertical is-parent">
                <div
                  class="tile is-child box"
                  :set="(user = challengeAttempt.user)"
                >
                  <p class="title is-5">{{ displayName(user) }}'s Submission</p>
                  <div class="media">
                    <div class="media-content">
                      <p class="subtitle is-7">Email</p>
                      <p class="title is-6">{{ user.email || 'No email' }}</p>
                    </div>
                    <div class="media-content">
                      <p class="subtitle is-7">Mobile</p>
                      <p class="title is-6">
                        {{ user.phoneNumber || 'No mobile' }}
                      </p>
                    </div>
                  </div>
                  <div class="media">
                    <div class="media-content">
                      <p class="subtitle is-7">Current Score</p>
                      <p class="title is-6">
                        {{ challengeAttempt.score.toFixed(2) || 0 }}
                      </p>
                    </div>
                    <div class="media-content">
                      <p class="subtitle is-7">Difficulty</p>
                      <p class="title is-6">
                        {{ challengeTypeNames[challengeAttempt.difficulty] }}
                      </p>
                    </div>
                    <div class="media-content">
                      <p class="subtitle is-7">ReReview</p>
                      <p
                        class="title is-6"
                        :class="[
                          challengeTypeNames.rereview
                            ? 'has-text-success'
                            : 'has-text-warning'
                        ]"
                      >
                        {{ challengeTypeNames.rereview ? 'Yes' : 'No' }}
                      </p>
                    </div>
                  </div>
                  <div class="media">
                    <div class="media-content">
                      <p class="subtitle is-7">Completion Status</p>
                      <p
                        class="title is-6"
                        :class="[
                          attemptCompleted
                            ? 'has-text-success'
                            : 'has-text-warning'
                        ]"
                      >
                        {{ attemptCompleted ? 'Completed' : 'Pending' }}
                      </p>
                    </div>
                    <div class="media-content">
                      <p class="subtitle is-7">Review Status</p>
                      <p
                        class="title is-6"
                        :class="[
                          attemptReviewed
                            ? 'has-text-success'
                            : 'has-text-warning'
                        ]"
                      >
                        {{ attemptReviewed ? 'Reviewed' : 'Pending' }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="tile is-parent">
                <div class="tile is-child box">
                  <p class="title is-5">Challenge: {{ challenge.name }}</p>
                  <div class="media">
                    <div class="media-content">
                      <p class="subtitle is-7">Start Time</p>
                      <p class="title is-6">
                        {{ parseDate(challenge.startTime) }}
                      </p>
                    </div>
                    <div class="media-content">
                      <p class="subtitle is-7">End Time</p>
                      <p class="title is-6">
                        {{ parseDate(challenge.endTime) }}
                      </p>
                    </div>
                    <div class="media-content">
                      <p class="subtitle is-7">Solutoion</p>
                      <p class="title is-6">
                        <mc-url-parse :src="challenge.details.solution">
                          <template #temp="{ src: url }">
                            <a target="x" :href="url">link</a>
                          </template>
                        </mc-url-parse>
                      </p>
                    </div>
                  </div>
                  <div class="media">
                    <div class="media-content">
                      <p class="subtitle is-7">Overall comment</p>
                      <p class="title is-6">
                        {{ challengeAttempt.overallComment }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section
              v-if="renderSubjectiveSubmissions"
              class="challenge-attempt__submission tile is-ancestor"
            >
              <div class="tile is-parent">
                <div class="tile is-child box">
                  <p class="title is-5">
                    {{ displayName(user) }}'s Subjective Submission
                  </p>
                  <carousel
                    class="challenge-attempt__submission__carousal"
                    :navigation-enabled="true"
                    :per-page="1"
                  >
                    <slide
                      v-for="url in challengeAttempt.submission"
                      :key="url"
                    >
                      <mc-url-parse :src="url">
                        <template #temp="{ src: url }">
                          <div class="image-container">
                            <img :src="url" />
                          </div>
                        </template>
                      </mc-url-parse>
                    </slide>
                  </carousel>
                </div>
              </div>
            </section>
            <review-question
              :questions="challengeAttempt.progress"
            ></review-question>
            <review-diff
              v-if="renderSubjectiveSubmissions"
              class="challenge-attempt__reviews"
              :reviews="challengeAttempt.reviews"
              :criteria="challenge.details.criteria"
              :overall-comment="challengeAttempt.overallComment"
            >
              <template #title>
                <p class="title is-5">{{ displayName(user) }}'s Reviews</p>
              </template>
            </review-diff>
          </div>
        </div>
      </div>
    </mc-app-body>
  </fragment>
</template>

<script>
import { challengeTypeNames } from './utils';
import { displayName } from '../../../utils/user';
import { DateTime as LuxonDateTime, LocalZone } from 'luxon';
import { Carousel, Slide } from 'vue-carousel';
import { mapState, mapActions } from 'vuex';
import ReviewDiff from './ReviewDiff';
import ReviewQuestion from './ReviewQuestion';

export default {
  data() {
    return {
      challengeTypeNames
    };
  },
  computed: {
    ...mapState(['challengeAttempts', 'challenges']),
    challenge() {
      return this.challenges.selected;
    },
    attemptCompleted() {
      return this.challengeAttempts.selected.score !== undefined;
    },
    attemptReviewed() {
      return this.hasReviews === 2;
    },
    renderSubjectiveSubmissions() {
      return (
        this.challengeAttempt.difficulty !== 0 && this.challengeAttempt.score
      );
    },
    hasReviews() {
      return Object.keys(this.challengeAttempt.reviews).length;
    }
  },
  methods: {
    ...mapActions('challengeAttempts', ['selectChallengeAttempt']),
    ...mapActions('challenges', ['selectChallenge']),
    displayName,
    parseDate(isoString) {
      return `${LuxonDateTime.fromISO(isoString).toLocaleString(
        LuxonDateTime.DATETIME_MED
      )}`;
    },
    loadChallengeAttempt(challengeAttemptId) {
      const challengeId = challengeAttemptId.split('_')[0];
      this.selectChallengeAttempt(challengeAttemptId);
      this.selectChallenge(challengeId);
    }
  },
  created() {
    this.loadChallengeAttempt(this.$route.params.id);
  },
  components: {
    Carousel,
    Slide,
    ReviewDiff,
    ReviewQuestion
  }
};
</script>

<style lang="scss" scoped>
.challenge-attempt {
  .media-content {
    white-space: normal;
  }

  &__submission {
    &__carousal {
      margin: 0 1em;
      .image-container {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>
